import { FC, useCallback, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { GiftCouponSummary } from '@shared/api/giftCoupon/giftCoupon.interface';
import { GiftDeliverySummary } from '@shared/api/giftDelivery/giftDelivery.interface';
import {
  giftOrderListForAdminDataAtomFamily,
  giftOrderListForAdminStatusAtomFamily,
} from '@shared/api/giftOrder/giftOrder.atom';
import { useGiftOrderListForAdminHook } from '@shared/api/giftOrder/giftOrder.hook';
import {
  GiftOrderDeliveryMode,
  GiftOrderForAdmin,
  GiftOrderPaymentStatus,
} from '@shared/api/giftOrder/giftOrder.interface';
import { Loading } from '@shared/components/Loading';
import { formatPrice } from '@shared/utils/formatPrice';

import { CouponDetailModal } from '../components/CouponDetailModal';
import { CreateCouponTestModal } from '../components/CreateCouponTestModal';
import { CreateDeliveryTestModal } from '../components/CreateDeliveryTestModal';
import { DeliveryDetailModal } from '../components/DeliveryDetailModal';
import { OrderRow } from '../components/OrderRow';
import { getOrderType, OrderTypeFilter } from '../utils/giftOrder.constants';

export const GiftOrderListPage: FC = function GiftOrderListPage() {
  const [statusFilter, setStatusFilter] = useState<'all' | 'paid' | 'confirmed'>('all');
  const [typeFilter, setTypeFilter] = useState<OrderTypeFilter>('all');
  const [modalCouponOrder, setModalCouponOrder] = useState<GiftOrderForAdmin | null>(null);
  const [modalDeliveryOrder, setModalDeliveryOrder] = useState<GiftOrderForAdmin | null>(
    null,
  );
  const [showDeliveryTestModal, setShowDeliveryTestModal] = useState(false);
  const [showCouponTestModal, setShowCouponTestModal] = useState(false);

  const handleCouponClick = useCallback(
    (_coupons: GiftCouponSummary[], order: GiftOrderForAdmin) => {
      setModalCouponOrder(order);
    },
    [],
  );

  const handleDeliveryClick = useCallback(
    (_deliveries: GiftDeliverySummary[], order: GiftOrderForAdmin) => {
      setModalDeliveryOrder(order);
    },
    [],
  );

  useGiftOrderListForAdminHook('default');
  const status = useRecoilValue(giftOrderListForAdminStatusAtomFamily('default'));
  const orderList = useRecoilValue(giftOrderListForAdminDataAtomFamily('default'));

  const filteredOrders = useMemo(() => {
    if (!orderList) return [];

    let filtered = [...orderList];

    if (statusFilter === 'paid') {
      filtered = filtered.filter(
        (order) => order.paymentDetail.status === GiftOrderPaymentStatus.Paid,
      );
    } else if (statusFilter === 'confirmed') {
      filtered = filtered.filter(
        (order) => order.paymentDetail.status === GiftOrderPaymentStatus.Confirmed,
      );
    }

    if (typeFilter === 'coupon') {
      filtered = filtered.filter((order) => {
        const type = getOrderType(order);
        return type === 'coupon' || type === 'mixed';
      });
    } else if (typeFilter === 'delivery') {
      filtered = filtered.filter((order) => {
        const type = getOrderType(order);
        return type === 'delivery' || type === 'mixed';
      });
    }

    return filtered;
  }, [orderList, statusFilter, typeFilter]);

  const stats = useMemo(() => {
    const empty = {
      total: 0,
      totalAmount: 0,
      couponCount: 0,
      couponAmount: 0,
      deliveryCount: 0,
      deliveryAmount: 0,
      individualCount: 0,
      individualAmount: 0,
      bulkCount: 0,
      bulkAmount: 0,
      mixedCount: 0,
      mixedAmount: 0,
    };
    if (!orderList) return empty;

    const s = { ...empty, total: orderList.length };

    orderList.forEach((order) => {
      const amount = order.paymentDetail.paymentByCash;
      s.totalAmount += amount;
      const type = getOrderType(order);
      if (type === 'coupon') {
        s.couponCount++;
        s.couponAmount += amount;
      } else if (type === 'delivery') {
        s.deliveryCount++;
        s.deliveryAmount += amount;
        if (order.deliveryMode === GiftOrderDeliveryMode.Individual) {
          s.individualCount++;
          s.individualAmount += amount;
        } else {
          s.bulkCount++;
          s.bulkAmount += amount;
        }
      } else {
        s.mixedCount++;
        s.mixedAmount += amount;
      }
    });

    return s;
  }, [orderList]);

  if (status === 'loading' || status === 'idle') {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* 헤더 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">결제 완료 주문</h1>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-lg bg-white p-3 shadow">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-medium text-gray-500">전체</p>
                <p className="text-lg font-bold text-gray-900">{stats.total}건</p>
              </div>
              <p className="text-xl font-bold text-gray-900">
                {formatPrice(stats.totalAmount)}원
              </p>
            </div>
          </div>
          <div
            className="cursor-pointer rounded-lg bg-white p-3 shadow ring-1 ring-purple-200 transition-shadow hover:shadow-md"
            onClick={() => setShowCouponTestModal(true)}
            title="[TEST] 클릭하여 Coupon 생성"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-medium text-purple-600">
                  쿠폰형
                  <span className="ml-1 text-[10px] text-red-400">[TEST]</span>
                </p>
                <p className="text-lg font-bold text-purple-700">{stats.couponCount}건</p>
              </div>
              <p className="text-xl font-bold text-purple-600">
                {formatPrice(stats.couponAmount)}원
              </p>
            </div>
          </div>
          <div
            className="cursor-pointer rounded-lg bg-white p-3 shadow ring-1 ring-blue-200 transition-shadow hover:shadow-md"
            onClick={() => setShowDeliveryTestModal(true)}
            title="[TEST] 클릭하여 Delivery 생성"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-medium text-blue-600">
                  배송형
                  <span className="ml-1 text-[10px] text-red-400">[TEST]</span>
                </p>
                <p className="text-lg font-bold text-blue-700">{stats.deliveryCount}건</p>
              </div>
              <p className="text-xl font-bold text-blue-600">
                {formatPrice(stats.deliveryAmount)}원
              </p>
              {stats.deliveryCount > 0 && (
                <div className="flex gap-3 border-l border-blue-100 pl-3 text-xs">
                  <div className="text-center">
                    <p className="text-teal-600">개별배송</p>
                    <p className="font-bold text-teal-700">{stats.individualCount}건</p>
                    <p className="font-medium text-teal-600">
                      {formatPrice(stats.individualAmount)}원
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-500">일괄배송</p>
                    <p className="font-bold text-gray-700">{stats.bulkCount}건</p>
                    <p className="font-medium text-gray-600">
                      {formatPrice(stats.bulkAmount)}원
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
          {stats.mixedCount > 0 && (
            <div className="rounded-lg bg-white p-3 shadow ring-1 ring-amber-200">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-medium text-amber-600">혼합</p>
                  <p className="text-lg font-bold text-amber-700">{stats.mixedCount}건</p>
                </div>
                <p className="text-xl font-bold text-amber-600">
                  {formatPrice(stats.mixedAmount)}원
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 필터 */}
      <div className="mb-4 flex flex-wrap gap-2">
        <div className="flex gap-2">
          <button
            onClick={() => setStatusFilter('all')}
            className={`rounded-lg px-4 py-2 text-sm font-medium ${
              statusFilter === 'all'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            전체
          </button>
          <button
            onClick={() => setStatusFilter('paid')}
            className={`rounded-lg px-4 py-2 text-sm font-medium ${
              statusFilter === 'paid'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            결제완료
          </button>
          <button
            onClick={() => setStatusFilter('confirmed')}
            className={`rounded-lg px-4 py-2 text-sm font-medium ${
              statusFilter === 'confirmed'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            확정
          </button>
        </div>

        <div className="mx-2 border-l border-gray-300" />

        <div className="flex gap-2">
          <button
            onClick={() => setTypeFilter('all')}
            className={`rounded-lg px-4 py-2 text-sm font-medium ${
              typeFilter === 'all'
                ? 'bg-gray-800 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            전체유형
          </button>
          <button
            onClick={() => setTypeFilter('coupon')}
            className={`rounded-lg px-4 py-2 text-sm font-medium ${
              typeFilter === 'coupon'
                ? 'bg-purple-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            쿠폰형
          </button>
          <button
            onClick={() => setTypeFilter('delivery')}
            className={`rounded-lg px-4 py-2 text-sm font-medium ${
              typeFilter === 'delivery'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            배송형
          </button>
        </div>
      </div>

      {/* 테이블 */}
      <div className="overflow-hidden rounded-lg bg-white shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  결제일시
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  상품명
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  유형
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  결제금액
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  주문자
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  고인명
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  상조
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium tracking-wider text-gray-500">
                  <span className="uppercase">쿠폰</span>
                  <span className="ml-1 text-[10px] font-normal normal-case text-gray-400">
                    (전체/미공유/미사용/교환)
                  </span>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium tracking-wider text-gray-500">
                  <span className="uppercase">배송</span>
                  <span className="ml-1 text-[10px] font-normal normal-case text-gray-400">
                    (전체/대기/준비/배송/완료)
                  </span>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  상태
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-3 py-8 text-center text-gray-500">
                    주문이 없습니다.
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => (
                  <OrderRow
                    key={order._id}
                    order={order}
                    onCouponClick={(coupons) => handleCouponClick(coupons, order)}
                    onDeliveryClick={(deliveries) => handleDeliveryClick(deliveries, order)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modalCouponOrder && modalCouponOrder.coupons && (
        <CouponDetailModal
          coupons={modalCouponOrder.coupons}
          orderId={modalCouponOrder._id}
          order={modalCouponOrder}
          onClose={() => setModalCouponOrder(null)}
        />
      )}

      {modalDeliveryOrder && modalDeliveryOrder.deliveries && (
        <DeliveryDetailModal
          deliveries={modalDeliveryOrder.deliveries as GiftDeliverySummary[]}
          orderId={modalDeliveryOrder._id}
          order={modalDeliveryOrder}
          onClose={() => setModalDeliveryOrder(null)}
        />
      )}

      {showDeliveryTestModal && orderList && (
        <CreateDeliveryTestModal
          orders={orderList}
          onClose={() => setShowDeliveryTestModal(false)}
        />
      )}

      {showCouponTestModal && orderList && (
        <CreateCouponTestModal
          orders={orderList}
          onClose={() => setShowCouponTestModal(false)}
        />
      )}
    </div>
  );
};
